import { useState } from 'react';
import { TicketCommentDto, TicketCommentScope } from '@sib/api';
import { Button, cn } from '@sib/ui';
import { extractNameFromEmail } from '@sib/utils';
import { Avatar, Tag } from '@spendbrain/common/components';
import { useClampOverflow } from '@spendbrain/common/components/UserSpendAreas/UserSpendAreas.utils';
import { useDateFormat } from '@spendbrain/core/utils/use-date-formats';
import { ChevronRightIcon } from '@spendbrain/icons';
import { useAppTranslation } from '@spendbrain/modules/tickets/hooks';
import { format } from 'date-fns';

interface SingleLogProps {
  log: TicketCommentDto;
}

export const SingleLog = ({ log }: SingleLogProps) => {
  const dateObj = new Date(log.createdAt);
  const [open, setOpen] = useState(false);
  const dateFormat = useDateFormat();
  const { t } = useAppTranslation();

  const date = format(dateObj, dateFormat.defaultDateFormat);
  const time = format(dateObj, dateFormat.defaultTimeFormat);

  const { ref: textRef, clamped: cutText } = useClampOverflow(true);

  const showToggle = cutText || open;

  const formatedName = extractNameFromEmail(log.createdBy.name);
  const isInternal = log.scope === TicketCommentScope.Internal;

  return (
    <div className="text-primary grid grid-cols-[250px_3fr_1fr_1fr] items-start gap-2 border-t px-1 pt-3 pb-4 transition hover:bg-gray-50">
      <div className="flex w-1/4 items-center gap-2">
        <Avatar
          avatarImageProps={{ avatarUrl: log.createdBy.avatarUrl }}
          avatarFallbackProps={{
            name: formatedName,
            className: 'bg-muted text-sm',
          }}
          className="size-12 self-start border-4 border-white bg-white shadow-md"
        />

        <div className="flex flex-col">
          <span className="truncate text-sm font-medium">{formatedName}</span>
          <span className="text-muted-foreground truncate text-xs">
            {log.createdBy.email}
          </span>
        </div>
      </div>
      <div
        className={cn(
          'flex h-full flex-1 flex-col justify-center text-sm',
          !cutText && ''
        )}
        onClick={() => showToggle && setOpen(!open)}
      >
        <p
          ref={textRef}
          className={cn('leading-snug break-all', !open && 'line-clamp-2')}
        >
          {log.comment}
        </p>
      </div>
      <div className="mr-1 flex h-full items-center justify-center">
        {isInternal && (
          <Tag
            LabelProps={{
              text: t('components.ticketDetails.comments.internalComment'),
            }}
            className="border-[#01183E] bg-[#E5EDFA] px-2 text-[#01183E]"
          />
        )}
      </div>
      <div className="text-muted-foreground flex h-full min-w-[80px] items-center justify-center gap-2 text-xs">
        <span>{date}</span>
        <span>{time}</span>
      </div>
      <div className="mt-2 mr-2 ml-auto flex items-center justify-center gap-2">
        {showToggle && (
          <Button
            variant="ghost"
            type="button"
            className="ml-5 size-5 p-0"
            onClick={() => setOpen(!open)}
          >
            <ChevronRightIcon
              className={cn(
                'size-5 transition-transform duration-300',
                open && 'rotate-90'
              )}
            />
          </Button>
        )}
      </div>
    </div>
  );
};
